import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Slider, Tooltip } from '@mui/material';
import { IDU_COLUMNS, POINT_OPTIONS, MAX_ATTEMPTS } from '../constants';

const CONTROLLABLE_COLUMNS = IDU_COLUMNS.filter(c => c.controllable);

function BulkCommandModal({ open, onClose, selectedCount, onSubmit, loading }) {
  const [pointName, setPointName] = useState(CONTROLLABLE_COLUMNS[0]?.key || '');
  const [value, setValue] = useState(null);
  const [maxAttempts, setMaxAttempts] = useState('');
  const [maxAttemptsError, setMaxAttemptsError] = useState('');

  useEffect(() => {
    if (open) {
      const defaultPoint = CONTROLLABLE_COLUMNS[0]?.key || '';
      setPointName(defaultPoint);
      setValue(defaultPoint === 'SetTemperature' ? 24 : null);
      setMaxAttempts('');
      setMaxAttemptsError('');
    }
  }, [open]);

  const maxLimit = MAX_ATTEMPTS[pointName] ?? 10;

  const handlePointChange = key => {
    setPointName(key);
    setValue(key === 'SetTemperature' ? 24 : null);
    setMaxAttempts('');
    setMaxAttemptsError('');
  };

  const handleMaxAttemptsChange = e => {
    const raw = e.target.value;
    setMaxAttempts(raw);
    if (raw === '') { setMaxAttemptsError(''); return; }
    const num = parseInt(raw, 10);
    if (isNaN(num) || num < 1 || num > maxLimit || String(num) !== raw) {
      setMaxAttemptsError(`Must be an integer between 1 and ${maxLimit}`);
    } else {
      setMaxAttemptsError('');
    }
  };

  const handleSubmit = () => {
    if (value === null || value === undefined) return;
    const ma = maxAttempts === '' ? undefined : parseInt(maxAttempts, 10);
    onSubmit({ pointName, value, maxAttempts: ma });
  };

  const canSubmit = value !== null && value !== undefined && !maxAttemptsError && !loading;

  const renderValueControl = () => {
    if (pointName === 'SetTemperature') {
      const temp = value ?? 24;
      return (
        <div style={{ padding: '12px 4px 4px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 36, fontWeight: 700, color: '#2050ff', fontFamily: 'Manrope' }}>
              {temp}°C
            </span>
          </div>
          <Slider
            min={18} max={30} step={1}
            value={temp}
            onChange={(_, v) => setValue(v)}
            valueLabelDisplay="auto"
            sx={{ color: '#2050ff', '& .MuiSlider-thumb': { width: 20, height: 20 }, '& .MuiSlider-rail': { opacity: 0.3 } }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#aaa', fontFamily: 'Manrope', marginTop: 4 }}>
            <span>18°C</span><span>30°C</span>
          </div>
        </div>
      );
    }
    const opts = POINT_OPTIONS[pointName] || [];
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 8 }}>
        {opts.map(opt => {
          const isActive = value === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setValue(opt.value)}
              style={{
                padding: '8px 18px',
                border: `2px solid ${isActive ? '#2050ff' : '#e5e7eb'}`,
                borderRadius: 8,
                background: isActive ? 'rgba(32,80,255,0.08)' : '#fff',
                color: isActive ? '#2050ff' : '#6b7280',
                fontWeight: isActive ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'Manrope',
                fontSize: 13,
                transition: 'all 0.15s',
                outline: 'none',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: 14, fontFamily: 'Manrope' } }}
    >
      <DialogTitle sx={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: 15, paddingBottom: 0 }}>
        Bulk Command
        <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6, fontSize: 13 }}>
          · {selectedCount} IDU{selectedCount !== 1 ? 's' : ''}
        </span>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: '12px !important' }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, fontWeight: 600, letterSpacing: '0.03em' }}>
            POINT
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {CONTROLLABLE_COLUMNS.map(col => {
              const isActive = pointName === col.key;
              return (
                <button
                  key={col.key}
                  onClick={() => handlePointChange(col.key)}
                  style={{
                    padding: '6px 14px',
                    border: `2px solid ${isActive ? '#2050ff' : '#e5e7eb'}`,
                    borderRadius: 6,
                    background: isActive ? 'rgba(32,80,255,0.08)' : '#fff',
                    color: isActive ? '#2050ff' : '#6b7280',
                    fontWeight: isActive ? 700 : 500,
                    cursor: 'pointer',
                    fontFamily: 'Manrope',
                    fontSize: 12,
                    outline: 'none',
                  }}
                >
                  {col.header}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600, letterSpacing: '0.03em' }}>
            VALUE
          </div>
          {renderValueControl()}
        </div>

        <div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, fontWeight: 600, letterSpacing: '0.03em', display: 'flex', alignItems: 'center', gap: 5 }}>
            MAX ATTEMPTS{' '}
            <span style={{ fontWeight: 400, color: '#aaa', textTransform: 'none', letterSpacing: 0 }}>
              (optional, 1–{maxLimit})
            </span>
            <Tooltip
              title="If a command fails, the system will retry sending it up to this many times before giving up."
              placement="top"
              arrow
            >
              <span style={{ display: 'flex', alignItems: 'center', cursor: 'default', color: '#9ca3af' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </span>
            </Tooltip>
          </div>
          <input
            type="number"
            min={1}
            max={maxLimit}
            step={1}
            value={maxAttempts}
            onChange={handleMaxAttemptsChange}
            placeholder="e.g. 3"
            style={{
              border: `1px solid ${maxAttemptsError ? '#ef4444' : '#e5e7eb'}`,
              borderRadius: 8,
              padding: '7px 12px',
              fontSize: 13,
              fontFamily: 'Manrope',
              outline: 'none',
              width: 120,
            }}
          />
          {maxAttemptsError && (
            <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{maxAttemptsError}</div>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ padding: '12px 20px 16px' }}>
        <button
          onClick={onClose}
          style={{
            padding: '8px 20px',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#fff',
            color: '#6b7280',
            cursor: 'pointer',
            fontFamily: 'Manrope',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            padding: '8px 22px',
            border: 'none',
            borderRadius: 8,
            background: canSubmit ? 'rgba(32,80,255,0.85)' : '#93a3d8',
            color: '#fff',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            fontFamily: 'Manrope',
            fontSize: 13,
            fontWeight: 700,
            transition: 'background 0.2s',
          }}
        >
          {loading ? 'Sending…' : 'Send Command'}
        </button>
      </DialogActions>
    </Dialog>
  );
}

export default BulkCommandModal;
